import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { FileText, Target, User, Calendar, ArrowLeft, Share2, ChevronDown, ChevronUp, CheckCircle2, Circle } from "lucide-react";
import { Progress } from "../components/ui/progress";
import Logo from "../components/Logo";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedPlanView() {
  const { shareId } = useParams();
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedWeek, setExpandedWeek] = useState(1);

  useEffect(() => {
    const fetchShare = async () => {
      try {
        const res = await axios.get(`${API}/shared-plans/${shareId}`);
        setShare(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "This shared plan was not found or has been revoked.");
      } finally {
        setLoading(false);
      }
    };
    fetchShare();
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-400 text-sm">Loading shared plan...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-0 shadow-xl" data-testid="shared-plan-error">
          <CardContent className="p-8 text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Share2 className="w-7 h-7 text-red-500" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Plan Not Available</h2>
            <p className="text-sm text-gray-500 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <ArrowLeft className="w-4 h-4 mr-1" /> Go to App
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const snap = share.plan_snapshot || {};
  const progress = share.progress_data;
  const planContent = snap.plan_content || "";

  // Simple week parser
  const weeks = parsePlanWeeks(planContent, snap.duration_weeks || 1);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <div className="hidden sm:block h-6 w-px bg-gray-200" />
            <span className="hidden sm:block text-xs text-gray-400 font-medium">Shared Plan</span>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" className="text-xs" data-testid="shared-plan-login-btn">
              Sign In
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-10">
        {/* Sharer info */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-4" data-testid="sharer-info">
          <User className="w-3.5 h-3.5" />
          <span>Shared by <strong className="text-gray-700">{share.sharer_name}</strong></span>
          <span className="text-gray-300">|</span>
          <Calendar className="w-3.5 h-3.5" />
          <span>{new Date(share.created_at).toLocaleDateString()}</span>
        </div>

        {/* Plan card */}
        <Card className="border-0 shadow-lg mb-6" data-testid="shared-plan-card">
          <CardHeader className="p-4 sm:p-6 pb-3">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 shrink-0">
                <FileText className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <CardTitle className="text-lg sm:text-xl font-bold text-gray-900">{snap.title}</CardTitle>
                {snap.description && (
                  <p className="text-xs sm:text-sm text-gray-500 mt-1 line-clamp-2">{snap.description}</p>
                )}
              </div>
            </div>

            {/* Meta tags */}
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-medium">
                <Target className="w-3 h-3" /> {snap.duration_weeks}w plan
              </span>
              {snap.difficulty_level && (
                <span className="text-[10px] sm:text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium capitalize">
                  {snap.difficulty_level}
                </span>
              )}
              {(snap.focus_areas || []).map((fa, i) => (
                <span key={i} className="text-[10px] sm:text-xs px-2 py-0.5 bg-violet-50 text-violet-700 rounded-full font-medium capitalize">
                  {fa.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          </CardHeader>

          {/* Progress section */}
          {progress && (
            <div className="px-4 sm:px-6 pb-4">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4" data-testid="shared-plan-progress">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-blue-800">Athlete Progress</span>
                  <span className="text-sm font-bold text-blue-700">{progress.completion_pct}%</span>
                </div>
                <Progress value={progress.completion_pct} className="h-2" />
                <p className="text-[10px] text-blue-600 mt-1.5">
                  {progress.total_completed} of {progress.total_days} days completed
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Message from sharer */}
        {share.message && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="sharer-message">
            <p className="text-xs font-medium text-amber-800 mb-1">Message from {share.sharer_name}:</p>
            <p className="text-sm text-amber-700 italic">&ldquo;{share.message}&rdquo;</p>
          </div>
        )}

        {/* Plan content by week */}
        {weeks.length > 0 ? (
          <div className="space-y-3" data-testid="shared-plan-weeks">
            {weeks.map((week) => (
              <Card key={week.num} className="border border-gray-200 shadow-sm">
                <button
                  data-testid={`shared-week-${week.num}`}
                  onClick={() => setExpandedWeek(expandedWeek === week.num ? null : week.num)}
                  className="w-full p-3 sm:p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-t-lg"
                >
                  <span className="text-sm font-bold text-gray-800">Week {week.num}</span>
                  {expandedWeek === week.num ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                {expandedWeek === week.num && (
                  <CardContent className="p-3 sm:p-4 pt-0 border-t border-gray-100">
                    <div className="prose prose-sm max-w-none text-gray-700 text-xs sm:text-sm whitespace-pre-line leading-relaxed">
                      {week.content}
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        ) : (
          planContent && (
            <Card className="border border-gray-200 shadow-sm">
              <CardContent className="p-4 sm:p-6">
                <div className="prose prose-sm max-w-none text-gray-700 text-xs sm:text-sm whitespace-pre-line leading-relaxed">
                  {planContent}
                </div>
              </CardContent>
            </Card>
          )
        )}
      </div>
    </div>
  );
}

function parsePlanWeeks(content, durationWeeks) {
  if (!content) return [];
  const weekRegex = /(?:^|\n)\s*(?:#+\s*)?(?:\*\*)?Week\s+(\d+)(?:\*\*)?[:\s\-]*/gi;
  const matches = [...content.matchAll(weekRegex)];
  if (matches.length === 0) {
    return [{ num: 1, content: content.trim() }];
  }
  const weeks = [];
  for (let i = 0; i < matches.length; i++) {
    const start = matches[i].index + matches[i][0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index : content.length;
    weeks.push({
      num: parseInt(matches[i][1]),
      content: content.slice(start, end).trim(),
    });
  }
  return weeks;
}
